<template>
  <div>
    <!-- <Header /> -->
    <FlashMessage v-if="this.messages.text != ''" :message="this.messages" />
    <div class="flex">
      <SideBar />

      <div
        class="w-[90%] mx-auto lg:w-full lg:mr-10 mt-10 rounded-3xl overflow-x-auto overflow-y-auto heightfix  bg-gradient-to-br from-white via-white to-blue-50/30 shadow-2xl"
      >
        <div class="px-5 lg:px-10 pt-8 pb-4">
          <div class="flex items-center justify-between mb-8">
            <div>
              <h1 class="text-2xl font-bold text-blue-600">Live Classes</h1>
              <p class="text-sm text-gray-500 mt-1">
                Join your scheduled live sessions
              </p>
            </div>
          </div>

          <div
            class="h-full mt-5 grid sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-8"
          >
            <div
              v-if="eventInfo.length == 0"
              class="col-span-full text-center py-16"
            >
              <div class="bg-gray-50 rounded-2xl p-12 inline-block">
                <svg
                  class="w-16 h-16 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p class="text-gray-500 text-lg">No Classes Scheduled</p>
                <p class="text-gray-400 text-sm mt-2">
                  Check back later for upcoming live sessions
                </p>
              </div>
            </div>

            <div
              v-for="events in eventInfo"
              :key="events.id"
              class="bg-white rounded-2xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300"
            >
              <div class="relative">
                <div
                  v-if="isToday(events.start_date)"
                  class="absolute top-4 left-4 z-10 bg-white py-1.5 px-4 rounded-full shadow-md flex items-center gap-2"
                >
                  <div
                    class="w-2 h-2 bg-red-500 rounded-full animate-pulse"
                  ></div>
                  <p class="text-xs font-semibold text-gray-700">
                    Today's Class
                  </p>
                </div>

                <div class="relative overflow-hidden rounded-t-2xl">
                  <img
                    v-if="events.image"
                    :src="events.image"
                    alt=""
                    srcset=""
                    class="w-full h-48 object-cover"
                  />
                  <img
                    v-else
                    class="w-full h-48 object-cover"
                    src="@/assets/new/events.png"
                    alt="Class thumbnail"
                  />
                  <div
                    class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"
                  ></div>
                </div>

                <div class="absolute bottom-4 right-4">
                  <div
                    v-if="isClassEnded(events.end_date)"
                    class="bg-red-500 text-white text-xs px-3 py-1.5 rounded-full shadow-md"
                  >
                    Ended
                  </div>
                  <div
                    v-else-if="!isClassStarted(events.start_date)"
                    class="bg-yellow-500 text-white text-xs px-3 py-1.5 rounded-full shadow-md"
                  >
                    Upcoming
                  </div>
                  <div
                    v-else
                    class="bg-green-500 text-white text-xs px-3 py-1.5 rounded-full shadow-md animate-pulse"
                  >
                    Live Now
                  </div>
                </div>
              </div>

              <div class="p-6">
                <h3
                  class="text-lg font-semibold text-gray-800 mb-3 line-clamp-2 min-h-[3.5rem]"
                >
                  {{ events.title }}
                </h3>

                <div class="space-y-2 mb-4">
                  <div class="flex items-center gap-2 text-sm">
                    <svg
                      class="w-4 h-4 text-red-500 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span class="text-gray-600"
                      >Starts: {{ formatDate(events.start_date) }}</span
                    >
                  </div>
                  <div class="flex items-center gap-2 text-sm">
                    <svg
                      class="w-4 h-4 text-red-500 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span class="text-gray-600"
                      >Ends: {{ formatDate(events.end_date) }}</span
                    >
                  </div>

                  <!-- Recording Link Display for Ended Classes -->
                  <div
                    v-if="isClassEnded(events.end_date) && events.recording"
                    class="mt-3 pt-2 border-t border-gray-100"
                  >
                    <div class="flex items-center gap-2 text-sm">
                      <svg
                        class="w-4 h-4 text-purple-500 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      <span class="text-gray-600 font-medium"
                        >Recording Available</span
                      >
                    </div>
                  </div>
                </div>

                <div class="mt-4">
                  <div
                    v-if="
                      isClassEnded(events.end_date) && events.feedback === true
                    "
                  >
                    <button
                      v-if="!events.has_feedback"
                      @click="openFeedback(events.id)"
                      class="w-full py-3 px-4 rounded-xl border-2 border-blue-500 text-blue-600 font-medium hover:bg-blue-50 flex items-center justify-center gap-2 transition-all duration-200"
                    >
                      <svg
                        class="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                        />
                      </svg>
                      <span>Give Feedback</span>
                    </button>

                    <button
                      v-else
                      @click="getfeedback(events.id)"
                      class="w-full py-3 px-4 rounded-xl border-2 border-green-500 text-green-600 font-medium hover:bg-green-50 flex items-center justify-center gap-2 transition-all duration-200"
                    >
                      <svg
                        class="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                        />
                        <path
                          d="M8 10H16M8 14H13"
                          stroke="white"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                      </svg>
                      <span>View Feedback</span>
                    </button>

                    <!-- Recording Button for Ended Classes -->
                    <div v-if="events.recording" class="mt-2">
                      <a
                        :href="events.recording"
                        target="_blank"
                        class="w-full py-3 px-4 rounded-xl bg-purple-500 text-white font-medium hover:bg-purple-600 flex items-center justify-center gap-2 transition-all duration-200 shadow-md"
                      >
                        <svg
                          class="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                          />
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>Watch Recording</span>
                      </a>
                    </div>
                  </div>

                  <div v-else-if="!isClassEnded(events.end_date)">
                    <!-- Class not started yet -->
                    <div
                      v-if="!isClassStarted(events.start_date)"
                      class="space-y-2"
                    >
                      <div
                        class="w-full py-3 px-4 rounded-xl bg-gray-100 text-gray-500 font-medium text-center cursor-not-allowed"
                      >
                        Join Class Disabled
                      </div>
                      <p class="text-xs text-gray-400 text-center">
                        Class starts on {{ formatDate(events.start_date) }}
                      </p>
                    </div>

                    <!-- Class is live -->
                    <div v-else>
                      <a
                        :href="events.url"
                        @click="joinClass(events)"
                        target="_blank"
                        class="block w-full py-3 px-4 rounded-xl bg-blue-500 text-white font-medium hover:bg-hover text-center shadow-md transition-all duration-200"
                      >
                        Join Class Now
                      </a>
                    </div>
                  </div>

                  <!-- Class Ended with Recording Only (No Feedback) -->
                  <div
                    v-else-if="
                      isClassEnded(events.end_date) &&
                      events.recording &&
                      events.feedback !== true
                    "
                  >
                    <a
                      :href="events.recording"
                      target="_blank"
                      class="w-full py-3 px-4 rounded-xl bg-purple-500 text-white font-medium hover:bg-purple-600 flex items-center justify-center gap-2 transition-all duration-200 shadow-md"
                    >
                      <svg
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>Watch Recording</span>
                    </a>
                  </div>

                  <div
                    v-else-if="
                      isClassEnded(events.end_date) && !events.recording
                    "
                    class="text-center py-3 px-4 rounded-xl bg-gray-100 text-gray-500 font-medium"
                  >
                    Class Ended
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="feedbackPopup"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="closeFeedbackModal"
    >
      <div
        class="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <!-- Modal Header -->
        <div
          class="flex items-center justify-between p-6 border-b border-gray-100"
        >
          <h3 class="text-xl font-bold text-blue-600">Submit Feedback</h3>
          <button
            @click="closeFeedbackModal"
            class="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
          >
            <svg
              class="w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6">
          <form @submit.prevent="scheduleFeedback" class="space-y-6">
            <!-- Description -->
            <div class="space-y-2">
              <label class="text-sm font-medium text-gray-700">
                Description <span class="text-red-500">*</span>
              </label>
              <textarea
                v-model="feedbackText"
                rows="4"
                minlength="10"
                required
                class="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 resize-none"
                placeholder="Share your thoughts about this class..."
              ></textarea>
               <p
                v-if="feedbackText && feedbackText.trim().length < 10"
                class="text-red-500 text-sm"
              >
                Description must be at least 10 characters.
              </p>
            </div>

            <!-- Rating -->
            <div class="space-y-2">
              <label class="text-sm font-medium text-gray-700">
                Rating <span class="text-red-500">*</span>
              </label>
              <div class="flex items-center gap-2">
                <template v-for="i in 5" :key="i">
                  <button
                    type="button"
                    @click="setRating(i)"
                    class="focus:outline-none"
                  >
                    <svg
                      :class="[
                        'w-8 h-8',
                        i <= selectedRating
                          ? 'text-yellow-400'
                          : 'text-gray-300 hover:text-yellow-200',
                      ]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                      />
                    </svg>
                  </button>
                </template>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="flex items-center justify-end gap-3 pt-4">
              <button
                type="button"
                @click="closeFeedbackModal"
                class="px-6 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="disable_submit || !isFeedbackValid"
                :class="[
                  'px-8 py-2.5 rounded-xl font-medium',
                  disable_submit || !isFeedbackValid
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-hover shadow-md',
                ]"
              >
                Submit Feedback
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div
      v-if="feedbackModalOpen"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="feedbackModalOpen = false"
    >
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div
          class="flex items-center justify-between p-6 border-b border-gray-100"
        >
          <h3 class="text-xl font-bold text-gray-800">Class Feedback</h3>
          <button
            @click="feedbackModalOpen = false"
            class="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
          >
            <svg
              class="w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div class="p-6 space-y-6">
          <div>
            <label class="text-sm font-medium text-gray-500 block mb-2">
              Description
            </label>
            <p class="text-gray-700 bg-gray-50 rounded-xl p-4">
              {{ feedback.text || "No description provided." }}
            </p>
          </div>

          <div>
            <label class="text-sm font-medium text-gray-500 block mb-2">
              Rating
            </label>
            <div class="flex items-center gap-1 bg-gray-50 rounded-xl p-4">
              <template v-for="i in 5" :key="i">
                <svg
                  :class="[
                    'w-6 h-6',
                    i <= feedback.rating ? 'text-yellow-400' : 'text-gray-300',
                  ]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                  />
                </svg>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import SideBar from "@/components/Student_preview/SideBar.vue";
import FlashMessage from "@/components/FlashMessage.vue";
export default {
  data() {
    return {
      eventInfo: "",
      messages: {
        text: "",
        type: "null",
      },
      courseDetails: null,
      current_date: new Date().toLocaleDateString("en-IN"),
      cr_date: new Date().toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
      fullDateTime: "",
      feedbackPopup: false,
      selectedRating: 0,
      selectedLiveClassId: null,
      feedbackText: "",
      disable_submit: false,
      feedback: {},
      feedbackModalOpen: false,
    };
  },

  props: ["course_id"],

  components: {
    SideBar,
    FlashMessage,
    // confirmPopup,
    // leftNavigationStudent,
    // VuePdfEmbed,
  },

  created() {
    this.geteventinfo();
    axios
      .get(`${process.env.VUE_APP_API}/courses/?Key=${this.studentdata.studentToken}`)
      .then((response) => (this.courseDetails = response.data.data));
  },

  computed: {
     ...mapGetters("student", [
      "studentdata"]),
       isFeedbackValid() {
      return (
        this.feedbackText &&
        this.feedbackText.length >= 10 &&
        this.selectedRating > 0
      );
    },
  },

  mounted() {
    const today = new Date();

    this.currentDate = String(today.getDate()).padStart(2, "0");
    this.currentMonth = String(today.getMonth() + 1).padStart(2, "0");
    this.currentYear = today.getFullYear();

    this.currentHours = String(today.getHours()).padStart(2, "0");
    this.currentMinutes = String(today.getMinutes()).padStart(2, "0");
    this.currentSeconds = String(today.getSeconds()).padStart(2, "0");

    this.fullDateTime = `${this.currentYear}-${this.currentMonth}-${this.currentDate} ${this.currentHours}:${this.currentMinutes}:${this.currentSeconds}`;
    console.log("fullDateTime", this.fullDateTime);
  },

  methods: {
    isClassStarted(startDateString) {
      const startDate = new Date(startDateString);
      const now = new Date();
      return now >= startDate;
    },

    isClassEnded(endDateString) {
      const endDate = new Date(endDateString);
      const now = new Date();
      return now > endDate;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },

    isToday(dateString) {
      const date = new Date(dateString);
      const today = new Date();
      return date.toDateString() === today.toDateString();
    },

    isClassEnded(endDateString) {
      return this.fullDateTime > endDateString;
    },

    closeFeedbackModal() {
      this.feedbackPopup = null;
      this.selectedRating = "";
      this.feedbackText = "";
    },

    joinClass(event) {
      const payload = {
        status: true,
      };
      axios.put(
        `${process.env.VUE_APP_API}/live-class-attendance/${event.attendance_slug}/?Key=${this.studentdata.studentToken}`,
        payload,
      );
    },

    openFeedback(id) {
      this.selectedLiveClassId = id;
      this.feedbackPopup = true;
    },
    async getfeedback(id) {
      const res = await axios.get(
        `${process.env.VUE_APP_API}/live_class-feedback/list/?Key=${this.studentdata.studentToken}&live_class__id=${id}&user_id__in=${this.studentdata.studentId}`,
      );
      this.feedback = res.data.data[0];
      this.feedbackModalOpen = true;
    },
    async scheduleFeedback() {
      if (this.disable_submit || !this.isFeedbackValid) return;
      this.disable_submit = true;
      const bodyVal = {
        user_id: this.studentdata.studentId,
        live_class: this.selectedLiveClassId,
        text: this.feedbackText,
        rating: this.selectedRating,
      };
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/live_class-feedback/create/?Key=${this.studentdata.studentToken}`,
          bodyVal,
        );
        if (response.status === 201 || response.status === 200) {
          this.messages = {
            text: "Feedback submitted successfully",
            type: "success",
          };

          this.feedbackPopup = false;
          this.feedbackText = "";
          this.selectedRating = 0;
          this.selectedLiveClassId = null;

          await this.geteventinfo();
        } else {
          this.messages = {
            text: "Feedback submission failed",
            type: "error",
          };
        }
      } catch (error) {
        console.error(error);
        this.messages = {
          text: "Something went wrong. Please try again.",
          type: "error",
        };
      } finally {
        this.disable_submit = false;
        setTimeout(() => {
          this.messages = { text: "", type: "null" };
        }, 3000);
      }
    },
    setRating(rating) {
      this.selectedRating = rating;
    },

    async getFilters() {
      this.eventInfo = [];

      let start_date = document.getElementById("start").value;
      let end_date = document.getElementById("end").value;

      await axios
        .get(
          `${process.env.VUE_APP_API}/calendar/view/?course_data__id=${this.course_id}&Key=${this.studentdata.studentToken}&end_date=${end_date}&start_date=${start_date}`,
        )
        .then((response) => {
          if (Object.keys(response.data) == "error") {
            console.log("Null");
          } else {
            this.eventInfo = response.data.data;
            this.eventInfo = this.eventInfo.filter((element) => {
              return (
                element.course_data == this.course_id ||
                element.course_data == null
              );
            });
            this.eventInfo.forEach((element) => {
              element["display_date"] = new Date(
                element.date,
              ).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              });
            });
            this.eventInfo = this.eventInfo.sort(function (el1, el2) {
              let x = new Date(el1.date),
                y = new Date(el2.date);
              return y - x;
            });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    async geteventinfo() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API}/calendar/view/?course_data__id=${this.course_id}&Key=${this.studentdata.studentToken}`,
        );
        if (Object.keys(res.data) == "error") {
          console.log("Null");
          return;
        }
        let events = res.data.data.filter((element) => {
          return (
            element.course_data == this.course_id || element.course_data == null
          );
        });

        events.forEach((element) => {
          element.display_date = new Date(element.date).toLocaleDateString(
            "en-IN",
            {
              day: "numeric",
              month: "long",
              year: "numeric",
            },
          );
        });

        await Promise.all(
          events.map(async (event) => {
            event.has_feedback = false;
            try {
              const feedbackRes = await axios.get(
                `${process.env.VUE_APP_API}/live_class-feedback/list/?Key=${this.studentdata.studentToken}&live_class__id=${event.id}&user_id__in=${this.studentdata.studentId}`,
              );
              if (
                feedbackRes.data &&
                feedbackRes.data.data &&
                feedbackRes.data.data.length > 0
              ) {
                event.has_feedback = true;
              }
            } catch (err) {
              console.log("Feedback check failed for class:", event.id, err);
            }
          }),
        );

        this.eventInfo = events.sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
      } catch (error) {
        console.log(error);
      }
    },
    bookevents(val) {
      if (val.studentlist == null) {
        let list_el = [];
        list_el.push(this.studentdata.studentId);
        val.studentlist = list_el;
      } else {
        let list_el1 = Object.values(val.studentlist);
        list_el1.push(this.studentdata.studentId);
        val.studentlist = list_el1;
      }
      const bookedvalues = {
        start_date: val.start_date,
        end_date: val.end_date,
        title: val.title,
        status: val.status,
        url: val.url,
        studentlist: val.studentlist,
        count: val.count,
        duration: val.duration,
        organization_id: val.organization_id,
        user: val.user,
        course_data: val.course_data,
      };

      axios
        .put(
          `${process.env.VUE_APP_API}/calendarupdated/${val.id}/?Key=${this.studentdata.studentToken}`,
          bookedvalues,
        )
        .then((response) => {
          if (response.statusText == "OK") {
            this.messages = {
              text: "Booked successfully",
              type: "success",
            };
          } else {
            this.messages = {
              text: " Event Faild",
              type: "error",
            };
          }
        });

      setTimeout(() => {
        this.messages = {
          text: " ",
          type: "null",
        };
        this.geteventinfo();
      }, 2000);
    },
  },
};
</script>

<style scoped>
.heightfix {
  height: calc(100vh - 390px) !important;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
